import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './LatestComments.translations.json';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useListLatestCommentsQuery } from './query/useGetLatestCommentsQuery';
import { LatestCommentsType, UserReadCommentType } from './schema/types';
import { formatDate, formatDateTime } from '../../common/utils';
import { Button, Checkbox, Flex } from 'antd';
import { useMutationErrors } from '../../common/queryClient';
import { GetApiErrorMessage } from '../../common/utils/ValidationMessagesMap';
import { useMutateUserReadCommentQuery } from './query/useMutateLatestCommentsQuery';
import {
  ErrorAlertWithDrawer,
  InfoIconUrl,
  Popconfirm,
  useNavigationBlockers,
} from '../../common/components';
import { ToggleUserReadComment } from './ToggleUserReadComment';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { TextLink } from '../../common/components/typography';
import * as Icon from 'react-feather';
import { expand } from 'uri-template-lite';
import {
  DataObjectDescriptionCommentsUrl,
  DataSubjectsCommentsUrl,
  SampleCommentsUrl,
} from '../index';
import styled from 'styled-components';
import { fontSizeSM } from '@atlas-frontend/design-tokens';
import { Table } from '../../common/tanstacktable';
import { Table as TableInstance } from '@tanstack/react-table';
import { getGroupDescriptionRef } from '../../dataobject';

const { addTranslations, t } = i18nNsFactory('latest-comments');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

function getHref(comment: LatestCommentsType) {
  if (comment.sampleId) {
    return expand(SampleCommentsUrl, {
      groupId: comment.dataObjectGroupId,
      date: comment.valueDate,
      subject: comment.subjectCode,
    });
  }
  if (comment.dataObjectGroupId && comment.dataObjectDescriptionId) {
    return expand(DataObjectDescriptionCommentsUrl, {
      groupDescriptionRef: getGroupDescriptionRef(
        comment.dataObjectGroupId,
        comment.dataObjectDescriptionId,
      ),
      valueDate: comment.valueDate,
    });
  }
  if (comment.dataSubjectId) {
    return expand(DataSubjectsCommentsUrl, {
      id: comment.dataSubjectId,
    });
  }
}

const columnHelper = createColumnHelper<LatestCommentsType>();
function getColumns(
  setCommentRead: (latestComment: LatestCommentsType) => void,
  setVisibleCommentsRead: () => void,
): ColumnDef<LatestCommentsType, any>[] {
  const columns: ColumnDef<LatestCommentsType, any>[] = [];
  columns.push(
    columnHelper.accessor('name', {
      header: t(`name.label`),
    }),
    columnHelper.accessor('valueDate', {
      header: t(`valueDate.label`),
      cell: (props) => formatDate(props.renderValue()),
    }),
    columnHelper.accessor(
      (rowData) =>
        rowData.subjectCode && rowData.subjectName
          ? `${rowData.subjectCode} - ${rowData.subjectName}`
          : '',
      {
        header: t(`subject.label`),
      },
    ),
    columnHelper.accessor((rowData) => formatDateTime(rowData.commentTime), {
      header: t(`commentTime.label`),
      cell: (props) => props.renderValue(),
      sortingFn: 'formattedDateTime',
    }),
    columnHelper.accessor('operations' as any, {
      header: () => (
        <Popconfirm
          id="confirm-set-visible-read"
          title={t('confirmation.title')}
          description={t('confirmation.description')}
          okText={t('confirmation.yes')}
          cancelText={t('confirmation.no')}
          onConfirm={setVisibleCommentsRead}
        >
          <StyledButton id="set-visible-read">
            <Flex justify="center">{t(`isRead.all`)}</Flex>
          </StyledButton>
        </Popconfirm>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => (
        <Flex
          justify="space-between"
          gap="small"
          key={`${props.row.original.sampleId}-${props.row.original.dataSubjectId}-${props.row.original.dataObjectDescriptionId}`}
        >
          <TextLink
            id={`classifier-mapping-${props.row.index}`}
            onClick={() =>
              props.row.original.read
                ? undefined
                : setCommentRead(props.row.original)
            }
            href={getHref(props.row.original)}
            target="_blank"
          >
            <Icon.Eye size={18} />
          </TextLink>
          <ToggleUserReadComment
            isRead={!!props.row.original.read}
            setCommentRead={() => setCommentRead(props.row.original)}
          />
        </Flex>
      ),
      meta: { width: 140 },
    }) as ColumnDef<LatestCommentsType, any>,
  );

  return columns;
}

export const LatestComments = () => {
  const {
    data: latestComments = [],
    refetch,
    isFetching,
  } = useListLatestCommentsQuery();
  const [showReadComments, setShowReadComments] = useState(false);

  const tableRef = useRef<TableInstance<LatestCommentsType> | null>(null);

  const filteredComments = showReadComments
    ? latestComments
    : latestComments.filter((comment) => !comment.read);

  const { clear: clearBlockers } = useNavigationBlockers();
  const { mutateAsync } = useMutateUserReadCommentQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const onSuccess = useCallback(() => {
    clearBlockers();
    void refetch();
  }, [clearBlockers, refetch]);

  const setCommentRead = useCallback(
    (latestComment: LatestCommentsType) => {
      clearMutationErrors();
      const userReadComment: UserReadCommentType = {
        userReadCommentId: latestComment.userReadCommentId,
        sampleId: latestComment.sampleId,
        dataObjectDescriptionId: latestComment.dataObjectDescriptionId,
        dataSubjectId: latestComment.dataSubjectId,
        commentTime: latestComment.commentTime,
      };

      return mutateAsync({ data: [userReadComment] })
        .then(onSuccess)
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [clearMutationErrors, mutateAsync, onSuccess, setMutationErrors],
  );

  const setVisibleCommentsRead = useCallback(() => {
    clearMutationErrors();
    const displayedRows = tableRef.current?.getFilteredRowModel().rows;
    const userReadComments: UserReadCommentType[] = (displayedRows || []).map(
      (row) => {
        const latestComment = row.original;
        return {
          sampleId: latestComment.sampleId,
          dataObjectDescriptionId: latestComment.dataObjectDescriptionId,
          dataSubjectId: latestComment.dataSubjectId,
          commentTime: latestComment.commentTime,
        } as UserReadCommentType;
      },
    );

    return mutateAsync({ data: userReadComments })
      .then(onSuccess)
      .catch((error) => {
        setMutationErrors(error.response?.data.errors ?? []);
        throw error;
      });
  }, [clearMutationErrors, mutateAsync, onSuccess, setMutationErrors]);

  const hangleShowReadComments = useCallback((e: CheckboxChangeEvent) => {
    setShowReadComments(e.target.checked);
  }, []);

  const columns: ColumnDef<LatestCommentsType, any>[] = useMemo(
    () => getColumns(setCommentRead, setVisibleCommentsRead),
    [setCommentRead, setVisibleCommentsRead],
  );
  return (
    <DashboardPanel
      title={t('title')}
      infoIconUrl={InfoIconUrl.LATEST_COMMENTS}
      hideEmptyImage
      additionalHeaderContent={
        <Checkbox
          id={'show-read-comments'}
          checked={showReadComments}
          onChange={hangleShowReadComments}
        >
          {t('showReadComments')}
        </Checkbox>
      }
      modalContent={
        <Flex vertical gap="small">
          <Checkbox
            id={'show-read-comments'}
            checked={showReadComments}
            onChange={hangleShowReadComments}
          >
            {t('showReadComments')}
          </Checkbox>
          <Table
            ref={tableRef}
            name="input-collection-table"
            columns={columns}
            data={filteredComments}
            hideClearFilters
            isLoading={isFetching}
            columnOrderingDisabled={true}
            columnResizeDisabled={true}
          />
        </Flex>
      }
    >
      {mutationErrors && (
        <ErrorAlertWithDrawer
          errorAction={t('action')}
          validationMessagesMap={mutationErrors}
        />
      )}
      <Table
        ref={tableRef}
        name="input-collection-table"
        columns={columns}
        data={filteredComments}
        hideClearFilters
        isLoading={isFetching}
        columnOrderingDisabled={true}
        columnResizeDisabled={true}
        hideEmptyImage
        sticky
        virtual
        scrollContainerSelector="#dashboard-panel-content"
      />
    </DashboardPanel>
  );
};

const StyledButton = styled(Button)`
  font-size: ${fontSizeSM}px;
  padding: 0 5px;
`;
